import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { PlusIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { OrgForm } from './-components/orgs/org-form'
import { OrgOverviewItem } from './-components/orgs/org-overview-item'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint } from './-components/shared/collapsible'
import { SectionLabel } from '@/components/custom/section-label'
import { ListPanelBodySkeleton } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/orgs/')({
  component: RouteComponent,
  pendingComponent: ListPanelBodySkeleton,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { t } = useLingui()
  const [searchQuery, setSearchQuery] = useState('')
  const [editOrgId, setEditOrgId] = useState<string | null>(null)
  const [openCreateOrg, setOpenCreateOrg] = useState(false)
  const { data: orgs, isFetching: orgsFetching } = useSuspenseQuery(context.orgsListQueryOptions)
  const orgsCount = orgs.length

  const orgQuery = searchQuery.toLowerCase()
  const filteredOrgs =
    searchQuery.trim() === '' ? orgs : orgs.filter((org) => org.name.toLowerCase().includes(orgQuery))

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-23 items-center gap-2 border-b px-4 py-3">
        <SearchInput placeholder={t`Search administrations...`} value={searchQuery} onChange={setSearchQuery} />
        <Button variant="secondary" onClick={() => setOpenCreateOrg(true)}>
          <PlusIcon className="size-4" />
          {t`Create administration`}
        </Button>
      </div>

      <ScrollableList
        isFetching={orgsFetching}
        isEmpty={filteredOrgs.length === 0}
        hint={orgsCount > 0 && <SelectionHint visible text={t`Select an administration to view its groups`} />}
        emptyState={
          searchQuery ? (
            <PanelEmptyState
              noResults
              icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
              title=""
              description=""
            />
          ) : (
            <PanelEmptyState
              icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
              title={t`No administrations yet`}
              description={t`Create your first administration to get started.`}
              action={
                <Button variant="secondary" onClick={() => setOpenCreateOrg(true)}>
                  <PlusIcon className="size-4" />
                  {t`Create administration`}
                </Button>
              }
            />
          )
        }
      >
        {orgsCount > 0 && (
          <SectionLabel className="px-2">
            {orgsCount === 1 ? t`1 administration` : t`${orgsCount} administrations`}
          </SectionLabel>
        )}
        {filteredOrgs.map((org) => (
          <OrgOverviewItem key={org.id} org={org} onEdit={() => setEditOrgId(org.id)} />
        ))}
      </ScrollableList>

      <Dialog open={openCreateOrg} onOpenChange={setOpenCreateOrg}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
          <OrgForm setOpen={setOpenCreateOrg} orgs={orgs} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOrgId} onOpenChange={(open) => !open && setEditOrgId(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
          {editOrgId && <OrgForm orgId={editOrgId} setOpen={(open) => !open && setEditOrgId(null)} orgs={orgs} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}
